// src/pages/OrderDetailsPage.jsx
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useUser } from '../context/UserContext';
import { orderService } from '../services/orderService';

const OrderDetailsPage = () => {
  const { globalOrderId } = useParams();
  const navigate = useNavigate();
  const { isLoggedIn } = useUser();
  const [details, setDetails] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isCancelling, setIsCancelling] = useState(false);
  
  // State for storing separate tracking responses indexed by subOrderId
  const [trackingMap, setTrackingDataMap] = useState({});

  // States for Return/Issue Modal
  const [issueModalOpen, setIssueModalOpen] = useState(false);
  const [issueTargetItem, setIssueTargetItem] = useState(null);
  const [issueData, setIssueData] = useState({ issueType: 'MISSING_ITEM', description: '' });
  const [isSubmittingIssue, setIsSubmittingIssue] = useState(false);

  useEffect(() => {
    if (!isLoggedIn()) {
      navigate('/login', { replace: true });
      return;
    }
    const fetchOrderDetails = async () => {
      try {
        const data = await orderService.getOrderDetails(globalOrderId);
        setDetails(data);
      } catch (err) {
        console.error("Failed to fetch order details", err);
      } finally {
        setLoading(false);
      }
    };
    fetchOrderDetails();
  }, [globalOrderId, isLoggedIn, navigate]);

  // Secondary effect mapping to trigger distinct live tracking requests per package
  useEffect(() => {
    if (details && details.orders) {
      details.orders.forEach(async (subOrder) => {
        if (subOrder.orderId && !['CANCELLED', 'DELIVERED'].includes(subOrder.orderStatus?.toUpperCase())) {
          try {
            const trackRes = await orderService.getOrderTracking(subOrder.orderId);
            if (trackRes) {
              setTrackingDataMap(prev => ({ ...prev, [subOrder.orderId]: trackRes }));
            }
          } catch (trackErr) {
            console.error(`Tracking error for package ${subOrder.orderId}:`, trackErr);
          }
        }
      });
    }
  }, [details]);

  const handleCancelOrder = async (id, type) => {
    let confirmMsg = '';
    if (type === 'GLOBAL') confirmMsg = "Are you sure you want to cancel the entire order?";
    else if (type === 'SUB') confirmMsg = "Are you sure you want to cancel this entire delivery package?";
    else confirmMsg = "Are you sure you want to cancel this specific item?";

    if (!window.confirm(confirmMsg)) return;

    setIsCancelling(true);
    try {
      if (type === 'ITEM') {
        await orderService.cancelOrderItem(id);
      } else {
        await orderService.cancelOrder(id); 
      }
      
      const data = await orderService.getOrderDetails(globalOrderId);
      setDetails(data);
    } catch (err) {
      console.error("Cancel error:", err);
      alert(err.response?.data?.message || "Failed to cancel. Please try again.");
    } finally {
      setIsCancelling(false);
    }
  };

  const openIssueModal = (item) => {
    setIssueTargetItem(item);
    setIssueData({ issueType: 'MISSING_ITEM', description: '' });
    setIssueModalOpen(true);
  };

  const handleIssueSubmit = async (e) => {
    e.preventDefault();
    if (!issueData.description.trim()) {
      alert("Please provide a description.");
      return;
    }
    
    setIsSubmittingIssue(true);
    try {
      await orderService.raiseOrderItemIssue(issueTargetItem.orderItemId, {
        issueType: issueData.issueType,
        description: issueData.description
      });
      alert("Return/Issue raised successfully!");
      setIssueModalOpen(false);
      
      // Refresh order details
      const data = await orderService.getOrderDetails(globalOrderId);
      setDetails(data);
    } catch (err) {
      console.error("Issue error:", err);
      alert(err.response?.data?.message || "Failed to raise issue. Please try again.");
    } finally {
      setIsSubmittingIssue(false);
    }
  };

  const formatDate = (dateString) => {
    const options = { year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' };
    return new Date(dateString).toLocaleDateString('en-IN', options);
  };

  const getStatusColor = (status) => {
    switch (status?.toUpperCase()) {
      case 'DELIVERED': return 'text-emerald-600 bg-emerald-50 border-emerald-200';
      case 'CANCELLED': return 'text-red-600 bg-red-50 border-red-200';
      case 'PROCESSING': return 'text-blue-600 bg-blue-50 border-blue-200';
      default: return 'text-orange-600 bg-orange-50 border-orange-200';
    }
  };

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center py-32">
        <i className="fas fa-spinner fa-spin text-4xl text-[#008A44] mb-4"></i>
        <p className="text-gray-500 font-medium">Fetching order details...</p>
      </div>
    );
  }

  if (!details) {
    return (
      <div className="text-center py-32 bg-white rounded-3xl mx-4 my-8 shadow-sm border border-gray-100">
        <i className="fas fa-exclamation-triangle text-6xl text-gray-300 mb-4"></i>
        <h2 className="text-2xl font-bold text-gray-800 mb-2">Order Not Found</h2>
        <p className="text-gray-500 mb-6">We couldn't find the details for this order.</p>
        <button onClick={() => navigate('/orders')} className="bg-[#008A44] text-white px-6 py-2.5 rounded-lg font-bold hover:bg-[#006e36]">
          Back to My Orders
        </button>
      </div>
    );
  }

  // FIXED: Fetch variables globally directly from API mapping
  const globalTotalTax = details.totalTax !== undefined ? details.totalTax : (details.orders?.reduce((sum, o) => sum + (o.totalTax || 0), 0) || 0);
  const globalDeliveryFee = details.deliveryFee !== undefined ? details.deliveryFee : (details.orders?.reduce((sum, o) => sum + (o.deliveryFee || 0), 0) || 0);
  const globalCodCharge = details.codCharge !== undefined ? details.codCharge : (details.orders?.reduce((sum, o) => sum + (o.codCharge || 0), 0) || 0);

  return (
    <div className="py-6 px-4 md:px-8 max-w-5xl mx-auto w-full min-h-[70vh]">
      
      <div className="flex items-center justify-between gap-4 mb-6 border-b border-gray-100 pb-5">
        <div className="flex items-center gap-4">
          <button onClick={() => navigate(-1)} className="w-10 h-10 rounded-full bg-white shadow-sm border border-gray-200 hover:bg-gray-50 flex items-center justify-center text-gray-600 transition-colors flex-shrink-0">
            <i className="fas fa-arrow-left"></i>
          </button>
          <div>
            <h1 className="text-xl md:text-2xl font-black text-[#008A44]">Order Details</h1>
            <p className="text-xs font-bold text-gray-400 mt-1">ID: #{details.globalOrderId}</p>
          </div>
        </div>
        
        {details.orders?.some(o => !['CANCELLED', 'DELIVERED'].includes(o.orderStatus?.toUpperCase())) && (
          <button 
            onClick={() => handleCancelOrder(details.globalOrderId, 'GLOBAL')}
            disabled={isCancelling}
            className="px-4 py-2 bg-red-50 text-red-600 hover:bg-red-100 rounded-xl text-xs sm:text-sm font-bold transition-colors flex items-center gap-2 shadow-sm border border-red-100 disabled:opacity-50 flex-shrink-0"
          >
            {isCancelling ? <i className="fas fa-spinner fa-spin"></i> : <i className="fas fa-times-circle"></i>}
            <span className="hidden sm:inline">Cancel Entire Order</span>
            <span className="sm:hidden">Cancel All</span>
          </button>
        )}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 lg:gap-8">
        
        {/* Left Column: Items */}
        <div className="lg:col-span-2 space-y-6">
          {details.orders && details.orders.map((subOrder, index) => {
            const isSubOrderQuick = subOrder.deliveryType?.toUpperCase() === 'QUICK';
            const liveTrack = trackingMap[subOrder.orderId];
            
            return (
              <div key={subOrder.orderId || index} className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
                
                <div className="bg-gray-50 border-b border-gray-100 p-4 flex justify-between items-center flex-wrap gap-2">
                  <div className="flex items-center gap-3">
                    <span className={`flex items-center gap-1.5 px-3 py-1 rounded-lg text-[10px] font-black uppercase tracking-widest border ${!isSubOrderQuick ? 'text-[#008A44] bg-emerald-50 border-emerald-200' : 'text-[#E32726] bg-[#FFC20E]/20 border-[#FFC20E]/40'}`}>
                      <i className={`fas ${!isSubOrderQuick ? 'fa-truck' : 'fa-bolt'}`}></i>
                      {subOrder.deliveryType} Delivery
                    </span>
                    <span className={`px-3 py-1 rounded-lg text-[10px] font-black uppercase tracking-widest border ${getStatusColor(subOrder.orderStatus)}`}>
                      {subOrder.orderStatus}
                    </span>
                  </div>
                  
                  <div className="flex items-center gap-3">
                    <p className="text-xs font-bold text-gray-500">Sub-Order: #{subOrder.orderId}</p>
                    {!['CANCELLED', 'DELIVERED'].includes(subOrder.orderStatus?.toUpperCase()) && (
                      <button 
                        onClick={() => handleCancelOrder(subOrder.orderId, 'SUB')}
                        disabled={isCancelling}
                        className="px-2 py-1 bg-white text-red-500 hover:bg-red-50 hover:border-red-200 rounded text-[10px] font-black uppercase tracking-wider transition-colors border border-gray-200 shadow-sm disabled:opacity-50"
                      >
                        Cancel Pack
                      </button>
                    )}
                  </div>
                </div>

                <div className="p-4 flex flex-col gap-4">
                  {subOrder.items && subOrder.items.map((item) => (
                    <div key={item.orderItemId} className="flex gap-4 items-start border-b border-gray-50 pb-4 last:border-0 last:pb-0">
                      <div className="w-20 h-20 rounded-xl bg-gray-50 border border-gray-100 flex items-center justify-center flex-shrink-0 overflow-hidden p-2">
                        <img 
                          src={item.image || 'https://images.unsplash.com/photo-1542838132-92c53300491e?w=150&q=80'} 
                          alt={item.productName} 
                          className="w-full h-full object-contain mix-blend-multiply"
                        />
                      </div>
                      <div className="flex-1">
                        <Link to={`/product/${item.productId}?vid=${item.outletVariantId || item.variantId || item.id}&mode=${subOrder.deliveryType || 'QUICK'}`} className="text-sm font-bold text-gray-900 leading-tight mb-1 hover:text-[#008A44] transition-colors line-clamp-2">
                          {item.productName}
                        </Link>
                        <p className="text-xs font-semibold text-gray-400 mb-2">{item.variantName}</p>
                        
                        <div className="flex items-end justify-between w-full">
                          <div className="flex items-center gap-2">
                            <span className="text-sm font-black text-gray-800">₹ {Math.round(item.sellingPrice)}</span>
                            <span className="text-xs font-bold text-gray-400">x {item.quantity}</span>
                          </div>
                          
                          <div className="flex flex-col items-end gap-1">
                            <span className="text-sm font-black text-[#008A44]">₹ {Math.round(item.total)}</span>
                            
                            {/* Cancel Logic */}
                            {!['CANCELLED', 'DELIVERED'].includes(subOrder.orderStatus?.toUpperCase()) && item.orderStatus?.toUpperCase() !== 'CANCELLED' && (
                              <button 
                                onClick={() => handleCancelOrder(item.orderItemId, 'ITEM')}
                                disabled={isCancelling}
                                className="text-[10px] font-bold text-red-400 hover:text-red-600 transition-colors uppercase tracking-wider disabled:opacity-50"
                              >
                                Cancel Item
                              </button>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>

                {/* LIVE ORDER TRACKING VISUAL STEPPER UI */}
                {liveTrack && (
                  <div className="mx-4 mb-4 p-4 bg-gray-50 rounded-2xl border border-gray-100">
                    <div className="flex items-center justify-between mb-3">
                      <h4 className="text-xs font-black text-gray-400 uppercase tracking-widest flex items-center gap-1.5">
                        <i className={`fas fa-map-marker-alt ${isSubOrderQuick ? 'text-[#E32726]' : 'text-[#008A44]'}`}></i> Live Package Status
                      </h4>
                      <span className="text-[10px] bg-white border border-gray-200 text-gray-600 font-extrabold px-2 py-0.5 rounded-md uppercase">
                        {liveTrack.deliveryStatus || 'Processing'}
                      </span>
                    </div>

                    {/* Horizontal Visual Pipeline Progress */}
                    <div className="flex items-center justify-between relative max-w-md mx-auto pt-3 pb-2">
                      <div className="absolute left-0 right-0 top-[22px] -translate-y-1/2 h-1 bg-gray-200 z-0 rounded-full"></div>
                      <div 
                        className={`absolute left-0 top-[22px] -translate-y-1/2 h-1 z-0 rounded-full transition-all duration-500 ${isSubOrderQuick ? 'bg-[#E32726]' : 'bg-[#008A44]'}`}
                        style={{ width: liveTrack.deliveryStatus === 'DELIVERED' ? '100%' : liveTrack.deliveryStatus === 'ASSIGNED' ? '50%' : '0%' }}
                      ></div>

                      {/* Step 1: Confirmed */}
                      <div className="flex flex-col items-center z-10 relative">
                        <div className={`w-6 h-6 rounded-full text-white flex items-center justify-center text-[10px] font-black shadow-sm ${isSubOrderQuick ? 'bg-[#E32726]' : 'bg-[#008A44]'}`}>
                          <i className="fas fa-check"></i>
                        </div>
                        <span className="text-[10px] font-extrabold text-gray-600 mt-1.5">Confirmed</span>
                      </div>

                      {/* Step 2: Assigned */}
                      <div className="flex flex-col items-center z-10 relative">
                        <div className={`w-6 h-6 rounded-full flex items-center justify-center text-[10px] font-black shadow-sm transition-all duration-300 ${['ASSIGNED', 'DELIVERED'].includes(liveTrack.deliveryStatus?.toUpperCase()) ? (isSubOrderQuick ? 'bg-[#E32726] text-white' : 'bg-[#008A44] text-white') : 'bg-white text-gray-300 border-2 border-gray-200'}`}>
                          <i className="fas fa-motorcycle"></i>
                        </div>
                        <span className="text-[10px] font-extrabold text-gray-600 mt-1.5">Rider Assigned</span>
                      </div>

                      {/* Step 3: Delivered */}
                      <div className="flex flex-col items-center z-10 relative">
                        <div className={`w-6 h-6 rounded-full flex items-center justify-center text-[10px] font-black shadow-sm transition-all duration-300 ${liveTrack.deliveryStatus?.toUpperCase() === 'DELIVERED' ? (isSubOrderQuick ? 'bg-[#E32726] text-white' : 'bg-[#008A44] text-white') : 'bg-white text-gray-300 border-2 border-gray-200'}`}>
                          <i className="fas fa-home"></i>
                        </div>
                        <span className="text-[10px] font-extrabold text-gray-600 mt-1.5">Delivered</span>
                      </div>
                    </div>

                    {/* Driver Profile Matrix card block */}
                    {liveTrack.driverName && (
                      <div className="flex items-center justify-between border-t border-gray-200/60 pt-3 mt-2 flex-wrap gap-2 text-xs">
                        <div className="flex items-center gap-2.5">
                          <div className={`w-9 h-9 rounded-full flex items-center justify-center font-black text-sm ${isSubOrderQuick ? 'bg-[#FFC20E]/20 text-[#E32726]' : 'bg-[#008A44]/10 text-[#008A44]'}`}>
                            <i className="fas fa-user"></i>
                          </div>
                          <div>
                            <p className="font-black text-gray-800">{liveTrack.driverName} {liveTrack.vehicleNumber ? `(${liveTrack.vehicleNumber})` : ''}</p>
                            <p className="text-[10px] font-bold text-gray-400 uppercase tracking-wide">{liveTrack.vehicleType || 'Delivery Executive'}</p>
                          </div>
                        </div>
                        {liveTrack.driverPhone && (
                          <a href={`tel:${liveTrack.driverPhone}`} className={`px-3 py-1.5 rounded-xl text-xs font-black flex items-center gap-1 transition-all shadow-sm ${isSubOrderQuick ? 'bg-[#FFC20E] text-gray-900 hover:bg-[#e0b20d]' : 'bg-[#008A44] text-white hover:bg-[#006e36]'}`}>
                            <i className="fas fa-phone-alt text-[10px]"></i> Call Rider
                          </a>
                        )}
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* Right Column: Order Summary */}
        <div className="space-y-6">
          <div className="bg-white p-6 rounded-3xl border border-gray-100 shadow-sm sticky top-[100px]">
            <h3 className="text-sm font-black text-gray-400 uppercase tracking-widest border-b border-gray-100 pb-3 mb-4">
              Payment Summary
            </h3>
            
            <div className="space-y-3 mb-4 text-sm">
              <div className="flex justify-between font-bold text-gray-600">
                <span>Date Placed</span>
                <span className="text-gray-900">{formatDate(details.orderedAt)}</span>
              </div>
              <div className="flex justify-between font-bold text-gray-600">
                <span>Total Items amount</span>
                <span className="text-gray-900">₹ {Math.round(details.totalAmount)}</span>
              </div>
              {details.discount > 0 && (
                <div className="flex justify-between font-bold text-emerald-500">
                  <span>Discount</span>
                  <span>- ₹ {Math.round(details.discount)}</span>
                </div>
              )}
              {globalTotalTax > 0 && (
                <div className="flex justify-between font-bold text-gray-600">
                  <span>Tax</span>
                  <span className="text-gray-900">₹ {Math.round(globalTotalTax)}</span>
                </div>
              )}
              {globalDeliveryFee > 0 && (
                <div className="flex justify-between font-bold text-gray-600">
                  <span>Delivery Fee</span>
                  <span className="text-gray-900">₹ {Math.round(globalDeliveryFee)}</span>
                </div>
              )}
              {globalCodCharge > 0 && (
                <div className="flex justify-between font-bold text-gray-600">
                  <span>COD Charge</span>
                  <span className="text-gray-900">₹ {Math.round(globalCodCharge)}</span>
                </div>
              )}
            </div>

            <div className="border-t-2 border-gray-100 pt-4 mb-4 flex justify-between items-center">
              <span className="text-base font-black text-gray-900 uppercase">Total Paid</span>
              <span className="text-2xl font-black text-[#008A44]">₹ {Math.round(details.payable)}</span>
            </div>

            <div className="flex items-center gap-3 p-3 bg-gray-50 border border-gray-200 rounded-xl text-xs font-bold text-gray-700">
              <div className="w-8 h-8 rounded-full bg-white flex items-center justify-center shadow-sm">
                <i className={`fas ${details.orders?.[0]?.paymentType === 'COD' ? 'fa-money-bill-wave text-orange-500' : 'fa-credit-card text-emerald-500'}`}></i>
              </div>
              <div>
                <p className="text-gray-400 uppercase tracking-widest text-[9px] mb-0.5">Payment Method</p>
                <p>{details.orders?.[0]?.paymentType === 'COD' ? 'Cash on Delivery' : 'Online Payment'}</p>
              </div>
            </div>
          </div>
        </div>
      </div>

    </div>
  );
};

export default OrderDetailsPage;